/**
 * ReelManagementPage — admin oversight of ALL reels (any visibility). Search
 * + paginated table + delete + toggle visibility + CSV/PDF export.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Video, Search, Download, Trash2, ChevronLeft, ChevronRight, X, Eye, ThumbsUp, EyeOff, ExternalLink, MessageCircle, TrendingUp, BarChart3 } from 'lucide-react'
import { useAuthStore } from '@/stores/authStore'
import { adminService, type AdminReelRow, type ContentCounts } from '@/services/adminService'
import { exportToCSV, exportToPDF, type ExportColumn } from '@/lib/exportUtils'
import { GovLayout, GovPageTitle, GovCard, GovButton, GovEmptyState } from '@/components/layout/GovLayout'

const visStyles: Record<string, string> = {
  PUBLIC: 'bg-green-100 text-green-700',
  FRIENDS: 'bg-amber-100 text-amber-700',
  PRIVATE: 'bg-gray-100 text-gray-700',
}

// Overview stat card — mirrors the Strishakti superadmin reel overview grid
// (gradient icon tile + label + big number).
const StatCard: React.FC<{ label: string; value: number; icon: React.ElementType; gradient: string }> = ({
  label, value, icon: Icon, gradient,
}) => (
  <div className="bg-white rounded-xl border border-gray-100 p-4 flex items-center gap-3 h-full">
    <div className={`w-10 h-10 rounded-lg flex items-center justify-center bg-gradient-to-br ${gradient} shrink-0`}>
      <Icon className="w-5 h-5 text-white" />
    </div>
    <div className="min-w-0 flex-1">
      <div className="text-xs text-gray-500 leading-tight">{label}</div>
      <div className="text-lg font-bold text-gray-900 leading-tight mt-0.5">{value.toLocaleString()}</div>
    </div>
  </div>
)

const ReelManagementPage: React.FC = () => {
  const { user, isAuthenticated } = useAuthStore()
  const navigate = useNavigate()

  const [rows, setRows] = useState<AdminReelRow[]>([])
  const [page, setPage] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [totalElements, setTotalElements] = useState(0)
  const [search, setSearch] = useState('')
  const [debounced, setDebounced] = useState('')
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState<number | null>(null)
  const [counts, setCounts] = useState<ContentCounts | null>(null)
  const [viewReel, setViewReel] = useState<AdminReelRow | null>(null)
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (!isAuthenticated) return navigate('/login')
    if (user && user.role !== 'ADMIN' && user.role !== 'SUPER_ADMIN') navigate('/feed')
  }, [user, isAuthenticated, navigate])

  useEffect(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current)
    debounceTimer.current = setTimeout(() => { setDebounced(search); setPage(0) }, 300)
    return () => { if (debounceTimer.current) clearTimeout(debounceTimer.current) }
  }, [search])

  const fetch = useCallback(async () => {
    if (!user) return
    setLoading(true)
    try {
      const data = await adminService.getAdminReels(debounced, page, 20)
      setRows(data.content)
      setTotalPages(data.totalPages)
      setTotalElements(data.totalElements)
    } catch (e) {
      console.error('fetch reels', e)
    } finally {
      setLoading(false)
    }
  }, [debounced, page, user])

  const fetchCounts = useCallback(async () => {
    try { setCounts(await adminService.getAdminReelCounts()) }
    catch (e) { console.error('fetch reel counts', e) }
  }, [])

  useEffect(() => { fetch() }, [fetch])
  useEffect(() => { fetchCounts() }, [fetchCounts])

  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this reel permanently? This cannot be undone.')) return
    setBusy(id)
    try { await adminService.deleteAdminReel(id); fetch(); fetchCounts() }
    catch (e) { console.error('delete reel', e); alert('Failed to delete reel.') }
    finally { setBusy(null) }
  }

  const handleToggle = async (id: number) => {
    setBusy(id)
    try {
      const updated = await adminService.toggleReelVisibility(id)
      setRows((prev) => prev.map((r) => (r.id === updated.id ? updated : r)))
      fetchCounts()
    } catch (e) {
      console.error('toggle reel', e)
      alert('Failed to toggle visibility.')
    } finally {
      setBusy(null)
    }
  }

  const exportColumns: ExportColumn<AdminReelRow>[] = [
    { header: 'ID', key: 'id' },
    { header: 'Creator', key: 'userName' },
    { header: 'Caption', key: 'caption', format: (v) => (v ? String(v).replace(/\s+/g, ' ').slice(0, 200) : '') },
    { header: 'Visibility', key: 'visibility' },
    { header: 'Views', key: 'viewCount' },
    { header: 'Likes', key: 'likeCount' },
    { header: 'Comments', key: 'commentCount' },
    { header: 'Shares', key: 'shareCount' },
    { header: 'Reshares', key: 'reshareCount' },
    { header: 'Created', key: 'createdAt', format: (v) => new Date(v as string).toLocaleString() },
  ]

  return (
    <GovLayout>
      <div className="space-y-4">
        <GovPageTitle title="Reel Management" subtitle={`${totalElements.toLocaleString()} reel${totalElements === 1 ? '' : 's'}`} icon={Video} />

        {/* Overview stat cards — Total / Views / Likes / Comments / Today / Avg */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <StatCard label="Total Reels" value={counts?.total ?? 0} icon={Video} gradient="from-purple-500 to-violet-600" />
          <StatCard label="Total Views" value={counts?.totalViews ?? 0} icon={Eye} gradient="from-blue-500 to-indigo-600" />
          <StatCard label="Total Likes" value={counts?.totalLikes ?? 0} icon={ThumbsUp} gradient="from-pink-500 to-rose-600" />
          <StatCard label="Comments" value={counts?.totalComments ?? 0} icon={MessageCircle} gradient="from-emerald-500 to-green-600" />
          <StatCard label="Today" value={counts?.today ?? 0} icon={TrendingUp} gradient="from-orange-500 to-amber-600" />
          <StatCard label="Avg Views" value={counts?.avgViews ?? 0} icon={BarChart3} gradient="from-cyan-500 to-teal-600" />
        </div>
        <div className="flex flex-wrap items-center gap-2 text-xs text-gray-600">
          <span className="font-medium text-gray-500">Visibility:</span>
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-green-100 text-green-700">Public {counts?.publicCount ?? 0}</span>
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-amber-100 text-amber-700">Friends {counts?.friendsCount ?? 0}</span>
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-gray-100 text-gray-700">Private {counts?.privateCount ?? 0}</span>
        </div>

        <GovCard>
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search reel caption…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-10 pr-10 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              {search && (
                <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700">
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            <div className="flex gap-2">
              <GovButton variant="secondary" size="sm" onClick={() => exportToCSV('reels', exportColumns, rows)} disabled={!rows.length}>
                <Download className="w-4 h-4 inline mr-1" /> CSV
              </GovButton>
              <GovButton variant="secondary" size="sm" onClick={() => exportToPDF('reels', 'Reels Report', exportColumns, rows)} disabled={!rows.length}>
                <Video className="w-4 h-4 inline mr-1" /> PDF
              </GovButton>
            </div>
          </div>

          {loading ? (
            <p className="text-sm text-gray-500 p-8 text-center">Loading…</p>
          ) : rows.length === 0 ? (
            <GovEmptyState icon="🎬" title="No Reels Found" description={search ? 'Try a different search.' : 'No reels have been created yet.'} />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="text-left p-4 text-sm font-semibold text-gray-600">Creator</th>
                    <th className="text-left p-4 text-sm font-semibold text-gray-600">Caption</th>
                    <th className="text-left p-4 text-sm font-semibold text-gray-600">Vis.</th>
                    <th className="text-left p-4 text-sm font-semibold text-gray-600">Stats</th>
                    <th className="text-left p-4 text-sm font-semibold text-gray-600">Created</th>
                    <th className="text-right p-4 text-sm font-semibold text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} className="border-t border-gray-100 hover:bg-gray-50">
                      <td className="p-4 font-medium text-gray-900 whitespace-nowrap">{r.userName || '—'}</td>
                      <td className="p-4 text-sm text-gray-700 max-w-xs truncate">{r.caption || <span className="text-gray-400">(no caption)</span>}</td>
                      <td className="p-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${visStyles[r.visibility] || visStyles.PRIVATE}`}>{r.visibility}</span>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-3 text-xs text-gray-600">
                          <span className="inline-flex items-center gap-1" title="Views"><Eye className="w-3.5 h-3.5" />{r.viewCount}</span>
                          <span className="inline-flex items-center gap-1" title="Likes"><ThumbsUp className="w-3.5 h-3.5" />{r.likeCount}</span>
                        </div>
                      </td>
                      <td className="p-4 text-sm text-gray-600 whitespace-nowrap">{new Date(r.createdAt).toLocaleDateString()}</td>
                      <td className="p-4 text-right">
                        <div className="inline-flex gap-1">
                          <button
                            onClick={() => setViewReel(r)}
                            title="View reel"
                            className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-blue-600 hover:bg-blue-50 rounded-lg"
                          >
                            <ExternalLink className="w-3.5 h-3.5" /> View
                          </button>
                          <button
                            onClick={() => handleToggle(r.id)}
                            disabled={busy === r.id}
                            title="Toggle visibility"
                            className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-100 rounded-lg disabled:opacity-50"
                          >
                            {r.visibility === 'PUBLIC' ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                          </button>
                          <button
                            onClick={() => handleDelete(r.id)}
                            disabled={busy === r.id}
                            className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 rounded-lg disabled:opacity-50"
                          >
                            <Trash2 className="w-3.5 h-3.5" /> {busy === r.id ? '…' : 'Delete'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="flex items-center justify-center gap-4 mt-4 pt-4 border-t border-gray-100">
                <GovButton variant="secondary" size="sm" onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0}>
                  <ChevronLeft className="w-4 h-4" /> Prev
                </GovButton>
                <span className="text-sm text-gray-600">Page {page + 1} of {Math.max(1, totalPages)} · {totalElements} total</span>
                <GovButton variant="secondary" size="sm" onClick={() => setPage((p) => p + 1)} disabled={page >= totalPages - 1}>
                  Next <ChevronRight className="w-4 h-4" />
                </GovButton>
              </div>
            </div>
          )}
        </GovCard>
      </div>

      {/* Reel detail modal — View opens an in-page popup (no redirect) with an
          embedded player + the same row actions (toggle visibility, delete)
          plus an "Open on site" link to the full page. */}
      {viewReel && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
          onClick={() => setViewReel(null)}
        >
          <div
            className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[85vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b border-gray-100 sticky top-0 bg-white rounded-t-xl">
              <h3 className="font-bold text-gray-900 flex items-center gap-2">
                <Video className="w-5 h-5 text-purple-600" /> Reel #{viewReel.id}
              </h3>
              <button onClick={() => setViewReel(null)} className="p-1 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-3">
              {viewReel.videoUrl ? (
                <video
                  src={viewReel.videoUrl}
                  poster={viewReel.thumbnailUrl}
                  controls
                  className="w-full rounded-lg bg-black max-h-[40vh]"
                />
              ) : (
                <div className="w-full h-40 flex items-center justify-center bg-gray-100 rounded-lg text-gray-400 text-sm">No video</div>
              )}
              <div className="flex items-center gap-2 text-sm">
                <span className="font-medium text-gray-900">{viewReel.userName || '—'}</span>
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${visStyles[viewReel.visibility] || visStyles.PRIVATE}`}>{viewReel.visibility}</span>
              </div>
              {viewReel.caption && (
                <p className="text-sm text-gray-700 whitespace-pre-wrap break-words bg-gray-50 rounded-lg p-3">{viewReel.caption}</p>
              )}
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-center">
                {[
                  { label: 'Views', value: viewReel.viewCount },
                  { label: 'Likes', value: viewReel.likeCount },
                  { label: 'Comments', value: viewReel.commentCount },
                  { label: 'Shares', value: viewReel.shareCount },
                  { label: 'Reshares', value: viewReel.reshareCount },
                ].map((s) => (
                  <div key={s.label} className="bg-gray-50 rounded-lg p-2">
                    <div className="text-lg font-bold text-gray-900">{s.value}</div>
                    <div className="text-xs text-gray-500">{s.label}</div>
                  </div>
                ))}
              </div>
              <div className="text-xs text-gray-500">Created {new Date(viewReel.createdAt).toLocaleString()}</div>
            </div>
            <div className="flex items-center justify-between gap-2 p-4 border-t border-gray-100 sticky bottom-0 bg-white rounded-b-xl">
              <div className="flex gap-2">
                <button
                  onClick={() => { navigate(`/reel/${viewReel.id}`); setViewReel(null) }}
                  className="inline-flex items-center gap-1 px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-100 rounded-lg"
                >
                  <ExternalLink className="w-3.5 h-3.5" /> Open on site
                </button>
                <button
                  onClick={() => { const id = viewReel.id; setViewReel(null); handleToggle(id) }}
                  disabled={busy === viewReel.id}
                  className="inline-flex items-center gap-1 px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-100 rounded-lg disabled:opacity-50"
                >
                  {viewReel.visibility === 'PUBLIC' ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                  {viewReel.visibility === 'PUBLIC' ? 'Make Private' : 'Make Public'}
                </button>
              </div>
              <button
                onClick={() => { const id = viewReel.id; setViewReel(null); handleDelete(id) }}
                disabled={busy === viewReel.id}
                className="inline-flex items-center gap-1 px-3 py-2 text-xs font-medium text-red-600 hover:bg-red-50 rounded-lg disabled:opacity-50"
              >
                <Trash2 className="w-3.5 h-3.5" /> {busy === viewReel.id ? 'Deleting…' : 'Delete reel'}
              </button>
            </div>
          </div>
        </div>
      )}
    </GovLayout>
  )
}

export default ReelManagementPage